import {observer} from 'mobx-react';
import React, {memo, useCallback, useEffect, useState, useMemo} from 'react';
import {Controller} from 'react-hook-form';
import {BaseURL} from '../../../../configs/request';
import {useStoreContext} from '../../../../store/store-context';
import {authStore} from '../../../../store/stores/auth-store';
import {Avatar} from '../../../../ui/atoms/avatar';
import {Button} from '../../../../ui/atoms/button';
import {Image} from '../../../../ui/atoms/image';
import {Label} from '../../../../ui/atoms/label';
import {VerticalSpacer} from '../../../../ui/atoms/spacer';
import {Body1, Body2} from '../../../../ui/atoms/typography';
import {TextField} from '../../../../ui/molecules/field/textfield';
import {activateNotification} from '../../../../ui/molecules/notification/activate-notification';
import styled from 'styled-components';
import {SectionCard} from '../../../../ui/layouts/section-card';
import {CalendarIcon} from '../../../../ui/atoms/icons';
import {Calendar} from '../../../../ui/molecules/calendar';
import {useDropDown} from '../../../../hooks';
import dayjs from 'dayjs';

import {SelectField} from '../../../../ui/molecules/select/selectfield';
import {PhoneSelectField} from '../../../../ui/molecules/select/phone-select-field';
import {ItemLoader} from '../../../../ui/organisms/item-loader';
import {ItemLoaderLine} from '../../../../ui/organisms/item-loader-line';
import {capitalize} from '../../../../utils';
import {useMemberProfileHook} from '../member-profile-hooks';
import {
  LeftChildInput,
  CustomButton,
  ProfileSaveChangesButton,
  ButtonWrapper,
  EmailButton,
  CancelButton,
  AvatarAction,
  DetailsActions,
  DetailsCard,
  DetailsGrid,
  FieldCell,
  ProfileAvatarEditor,
  ProfileIdentity,
  ProfileName,
  ProfileRole,
  ProfileStack,
  ProfileSummary,
  ProfileSummaryCard,
  SectionIntro,
} from '../member-profile-style';
import {usePhoneCodes} from '../../../../utils/phone-codes';
import {getTimezoneOptions, normalizeTimezoneIdentifier} from '@utils/timezone';
import {MultiOptionsSelectField2} from '../../../../ui/molecules/select/multi-options-2';
import {UserAPIRequestImpl} from '../../../../api/user-api/user-api-request';
import {UserAPIController} from '../../../../api/user-api/user-api-controller';
import {
  ActiveVacation,
  buildEndedVacationPayload,
  buildVacationPayload,
  formatVacationDate,
  getVacationDateKey,
  isVacationEndDateValid,
  normalizeVacationResponse,
} from '@utils/vacation';
import {getColorFromTheme} from '../../../../ui/ui-utils';
import {useQueryClient} from 'react-query';
import {currentUserVacationQueryKey} from '../../../../routes/use-header-vacation';
import {mergeRefreshedUserIntoAuth} from './personal-auth';

const userAPIController = new UserAPIController(new UserAPIRequestImpl());

export const VacationSection = styled.div`
  padding: 22px 24px;
  border-radius: 10px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('white')};
  min-width: 0;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const VacationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const VacationStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  color: ${getColorFromTheme('green300')};
  background: ${getColorFromTheme('backgroundLight')};
  font-size: 12px;
  font-weight: 500;
`;

const VacationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const DateField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 340px;
  min-width: 0;

  @media (max-width: 992px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const DateLabel = styled.div`
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
  padding: 14px 16px;
  background: ${getColorFromTheme('backgroundDark')};
  white-space: nowrap;
  min-width: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  box-sizing: border-box;
`;

export const CustomDatePickerContainer = styled.div<{disabled?: boolean}>`
  position: relative;
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.disabled
        ? getColorFromTheme('borderLight')
        : getColorFromTheme('borderDark')};
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${(props) =>
    props.disabled
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  gap: 8px;
  flex: 1;
  height: 48px;
  width: auto;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 992px) {
    width: auto;
    min-width: 0;
  }

  @media (max-width: 768px) {
    width: 100%;
    flex: none;
  }

  svg {
    stroke: ${(props) =>
      props.disabled
        ? getColorFromTheme('textMuted')
        : getColorFromTheme('textBody')};
  }
`;

const DropdownWrapper = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10;
`;

const SaveButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 600px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const DangerButton = styled(Button)`
  color: ${getColorFromTheme('red400')};
  background: ${getColorFromTheme('white')};
  border-color: ${getColorFromTheme('red400')};

  &:not(:disabled):hover,
  &:not(:disabled):focus {
    color: ${getColorFromTheme('red400')};
    border-color: ${getColorFromTheme('red400')};
  }
`;

const VacationReason = styled.div`
  padding-top: 20px;
`;

const VacationReasonValue = styled.div`
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundLight')};
`;

const ResponsiveGroupField = styled.div`
  width: 100%;
  min-width: 0;

  button {
    height: auto;
    min-height: 52px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  button > div {
    gap: 8px;
  }

  button > div > span:first-child {
    flex: 1;
    min-width: 0;
  }

  button > div > span:last-child {
    flex: 0 0 auto;
  }
`;

export const GroupsMultiselect = memo(
  ({
    control,
    handleSelectChangeGroups,
    group,
    currentUserGroups,

    feature,
    disabled,
    name,
    label,
    placeholder,
    helper,
    state,
  }: any) => {
    return (
      <Controller
        control={control}
        name={name ? name : 'groups'}
        render={({onBlur, value}) => (
          <MultiOptionsSelectField2
            onBlur={onBlur}
            value={value}
            defaultValue={currentUserGroups?.map((grp: any) => ({
              label: grp.name,
              value: grp.id,
            }))}
            name={name ? name : 'groups'}
            label={
              feature === 'invite'
                ? undefined
                : feature !== 'invite' && label
                ? label
                : 'Group(s)'
            }
            onChange={(data: {value: string; label: string}[]) =>
              handleSelectChangeGroups(data)
            }
            helper={helper}
            state={state}
            placeholder={placeholder ? placeholder : 'Select groups'}
            options={group.filter((grp: any) => !grp.label.includes('General'))}
            disabled={disabled}
          />
        )}
      />
    );
  },
);

export const Personal = observer(() => {
  const {
    register,
    errors,
    isSubmitting,
    isSubmittingEmail,
    submitForm,
    submitEmail,
    handleSelectChange,
    handleSelectChangeGroups,
    handleSubmit,
    control,
    image,
    base64Upload,
    handleChange,
    isDisabled,
    emailRegister,
    emailErrors,
    emailHandleSubmit,
    //currentUser,
    isFetchingCurrentUser,
    group,
    currentUserGroups,
    handleSetPrefix,
  } = useMemberProfileHook();
  const phoneCodes = usePhoneCodes();
  const phoneCodeOptions = useMemo(
    () =>
      phoneCodes
        .map((country) => ({
          label: country.name,
          value: country.dial_code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [phoneCodes],
  );

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const handleClick = (e: any) => {
    hiddenFileInput?.current?.click();
    e.preventDefault();
  };
  const [progress, setProgress] = useState(false);
  const {usersStore} = useStoreContext();
  const {user} = usersStore;
  const currentUser = user;

  //Upload avatar
  const uploadAvatar = async (e: any) => {
    e.preventDefault();
    let avatarData = {
      avatar: base64Upload,
    };
    setProgress(true);
    await fetch(`${BaseURL}/users/${authStore.auth?.user.id}/upload-avatar`, {
      mode: 'cors',
      method: 'POST',
      headers: {
        'content-Type': 'application/json',
        Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
      },
      body: JSON.stringify(avatarData),
    })
      .then((res) => {
        if (res.ok) {
          activateNotification({
            title: 'Success',
            content: 'Avatar uploaded successfully',
            kind: 'success',
          });
        } else {
          activateNotification({
            title: 'Error',
            content: res.statusText,
            kind: 'error',
          });
        }
        return res.json();
      })
      .finally(() => setProgress(false));
  };
  const [disabled, setDisabled] = useState(true);

  const [data, setData] = useState('');

  const isDisabledEmail =
    data === currentUser?.email || data.length === 0 ? true : false;

  const getEmail = (val: any) => {
    setData(val.target.value);
  };
  const emailChange = (e: any) => {
    e.preventDefault();
    setDisabled(!disabled);
  };
  const [countryCode, setCountryCode] = useState<string>(
    currentUser?.countryCode ? currentUser?.countryCode : '+1',
  );
  const onChangeCountryCode = (prefix: {label: string; value: string}) => {
    setCountryCode(prefix.value);
  };
  useEffect(() => {
    if (currentUser?.countryCode && countryCode !== currentUser?.countryCode) {
      setCountryCode(currentUser?.countryCode);
    }
  }, [countryCode, currentUser?.countryCode]);

  const GroupSelect = useCallback(
    ({control, currentUser, group, handleSelectChangeGroups}) => {
      return (
        <GroupsMultiselect
          control={control}
          handleSelectChangeGroups={handleSelectChangeGroups}
          group={group}
          currentUser={currentUser}
          currentUserGroups={currentUserGroups}
        />
      );
    },
    [currentUserGroups],
  );

  const [activeVacation, setActiveVacation] = useState<ActiveVacation | null>(
    null,
  );
  const queryClient = useQueryClient();
  const [vacationEndDate, setVacationEndDate] = useState('');
  const [vacationReason, setVacationReason] = useState('');
  const [isEditingVacation, setIsEditingVacation] = useState(false);
  const [isVacationLoading, setIsVacationLoading] = useState(true);
  const [isSavingVacation, setIsSavingVacation] = useState(false);
  const [isEndingVacation, setIsEndingVacation] = useState(false);

  const endDateDropdown = useDropDown();

  const handleEndDateSelect = useCallback(
    (date: Date) => {
      setVacationEndDate(dayjs(date).format('YYYY-MM-DD'));
      endDateDropdown.handleClose();
    },
    [endDateDropdown],
  );

  const syncCurrentUser = useCallback(async () => {
    const refreshedUser = await userAPIController.fetchCurrentUser();
    if (!refreshedUser) return null;

    usersStore.patchCurrentUser(refreshedUser);
    await usersStore.setCurrentUser(refreshedUser);
    if (authStore.auth) {
      const updatedAuth = mergeRefreshedUserIntoAuth(
        authStore.auth,
        refreshedUser,
      );
      authStore.setAuth(updatedAuth);
      authStore.storeAuthInLocalStorage(updatedAuth);
    }
    return refreshedUser;
  }, [usersStore]);

  const setLocalVacationStatus = useCallback(
    async (onVacation: boolean) => {
      if (!currentUser) return;

      const updatedUser = {...currentUser, onVacation};
      usersStore.patchCurrentUser(updatedUser);
      await usersStore.setCurrentUser(updatedUser);

      if (authStore.auth) {
        const updatedAuth = mergeRefreshedUserIntoAuth(
          authStore.auth,
          updatedUser,
        );
        authStore.setAuth(updatedAuth);
        authStore.storeAuthInLocalStorage(updatedAuth);
      }
    },
    [currentUser, usersStore],
  );

  const confirmVacationStatus = useCallback(
    async (expectedStatus: boolean) => {
      if (!currentUser?.id) return false;

      let refreshedUser = await syncCurrentUser();
      if (refreshedUser?.onVacation !== expectedStatus) {
        const fallbackResponse = await userAPIController.updateUser(
          {onVacation: expectedStatus},
          currentUser.id,
          true,
        );
        if (fallbackResponse === false) return false;
        refreshedUser = await syncCurrentUser();
      }

      return refreshedUser?.onVacation === expectedStatus;
    },
    [currentUser?.id, syncCurrentUser],
  );

  const loadActiveVacation = useCallback(async () => {
    if (!currentUser?.id) return;

    setIsVacationLoading(true);
    if (currentUser.onVacation === false) {
      setActiveVacation(null);
      queryClient.setQueryData(
        currentUserVacationQueryKey(currentUser.id),
        null,
      );
      setVacationEndDate('');
      setVacationReason('');
      setIsEditingVacation(false);
      setIsVacationLoading(false);
      return;
    }

    const response = await userAPIController.fetchUserVacation(currentUser.id);
    const vacation =
      response === false ? null : normalizeVacationResponse(response);

    setActiveVacation(vacation);
    queryClient.setQueryData(
      currentUserVacationQueryKey(currentUser.id),
      vacation,
    );
    setVacationEndDate(
      vacation?.endDate
        ? getVacationDateKey(
            vacation.endDate,
            normalizeTimezoneIdentifier(currentUser?.timezone),
          )
        : '',
    );
    setVacationReason(vacation?.reason || '');
    setIsEditingVacation(false);
    setIsVacationLoading(false);
  }, [
    currentUser?.id,
    currentUser?.onVacation,
    currentUser?.timezone,
    queryClient,
  ]);

  useEffect(() => {
    loadActiveVacation();
  }, [loadActiveVacation]);

  const handleSaveVacation = async () => {
    const startDate = activeVacation?.startDate || dayjs().format('YYYY-MM-DD');
    if (!isVacationEndDateValid(vacationEndDate, startDate)) return;

    setIsSavingVacation(true);
    try {
      const vacationData = buildVacationPayload({
        activeVacation,
        endDate: vacationEndDate,
        reason: vacationReason,
        timezone: normalizeTimezoneIdentifier(currentUser?.timezone),
      });
      const response = await userAPIController.updateUserVacation(
        vacationData,
        currentUser?.id || '',
      );
      if (response === false) return;

      const isActive = await confirmVacationStatus(true);
      if (!isActive) {
        activateNotification({
          title: 'Error',
          content: 'Out of Office could not be activated. Please try again.',
          kind: 'error',
        });
        return;
      }

      const nextVacation = normalizeVacationResponse(response) || vacationData;
      setActiveVacation(nextVacation);
      queryClient.setQueryData(
        currentUserVacationQueryKey(currentUser?.id),
        nextVacation,
      );
      setIsEditingVacation(false);

      activateNotification({
        title: 'Success',
        content: activeVacation
          ? 'Out of Office dates updated'
          : 'Out of Office is now active',
        kind: 'success',
      });
    } finally {
      setIsSavingVacation(false);
    }
  };

  const handleEndVacation = async () => {
    if (!currentUser?.id) return;

    setIsEndingVacation(true);
    try {
      const response = await userAPIController.endUserVacation(
        currentUser.id,
        true,
      );
      let hasEnded = response !== false;
      if (!hasEnded && activeVacation) {
        const expirationResponse = await userAPIController.updateUserVacation(
          buildEndedVacationPayload(activeVacation),
          currentUser.id,
        );
        if (expirationResponse === false) return;
        hasEnded = true;
      }

      if (!hasEnded) {
        activateNotification({
          title: 'Error',
          content: 'Out of Office could not be ended. Please try again.',
          kind: 'error',
        });
        return;
      }

      await setLocalVacationStatus(false);
      setActiveVacation(null);
      queryClient.setQueryData(
        currentUserVacationQueryKey(currentUser.id),
        null,
      );
      setVacationEndDate('');
      setVacationReason('');
      setIsEditingVacation(false);
      activateNotification({
        title: 'Success',
        content: 'Out of Office ended',
        kind: 'success',
      });
    } finally {
      setIsEndingVacation(false);
    }
  };

  const handleCancelVacationEdit = () => {
    setVacationEndDate(
      activeVacation?.endDate
        ? getVacationDateKey(
            activeVacation.endDate,
            normalizeTimezoneIdentifier(currentUser?.timezone),
          )
        : '',
    );
    setVacationReason(activeVacation?.reason || '');
    setIsEditingVacation(false);
  };

  return (
    <>
      {!isFetchingCurrentUser && (
        <ProfileStack>
          <ProfileSummaryCard>
            <ProfileSummary>
              <ProfileAvatarEditor>
                <div>
                  {image.preview ? (
                    <Image
                      src={image.preview}
                      alt="Profile preview"
                      width="80"
                      height="80"
                      style={{borderRadius: '50%', objectFit: 'cover'}}
                    />
                  ) : (
                    <Avatar
                      alt="Profile image"
                      name={`${currentUser?.firstName || ''} ${
                        currentUser?.lastName || ''
                      }`}
                      size="xl"
                      src={currentUser?.avatar?.url}
                      userId={currentUser?.id}
                      tooltip={false}
                    />
                  )}
                </div>
                {image.preview ? (
                  <AvatarAction
                    type="button"
                    kind="primary"
                    onClick={uploadAvatar}
                    isLoading={progress}
                    aria-busy={progress}>
                    Save
                  </AvatarAction>
                ) : (
                  <AvatarAction
                    type="button"
                    kind="secondary"
                    onClick={handleClick}>
                    Edit
                  </AvatarAction>
                )}
                <LeftChildInput
                  type="file"
                  ref={hiddenFileInput}
                  id="edit"
                  accept="image/*"
                  onChange={handleChange}
                />
              </ProfileAvatarEditor>
              <ProfileIdentity>
                <ProfileName>
                  {currentUser?.firstName && currentUser?.lastName
                    ? `${capitalize(currentUser.firstName)} ${capitalize(
                        currentUser.lastName,
                      )}`
                    : '----- -----'}
                </ProfileName>
                <ProfileRole>
                  {currentUser?.role === 'admin' ? 'Admin' : 'Member'}
                </ProfileRole>
              </ProfileIdentity>
            </ProfileSummary>
          </ProfileSummaryCard>

          <DetailsCard>
            <SectionIntro>
              <h2>Personal details</h2>
              <p>Your basic profile information visible to your team.</p>
            </SectionIntro>
            <form method="patch" onSubmit={handleSubmit(submitForm)}>
              <DetailsGrid>
                <FieldCell>
                  <TextField
                    label="First Name"
                    placeholder={currentUser?.firstName || 'First Name'}
                    type="text"
                    autoComplete="text"
                    defaultValue={currentUser?.firstName || ''}
                    ref={register}
                    helper={errors.firstName?.message || ''}
                    state={errors.firstName ? 'error' : 'default'}
                    name="firstName"
                  />
                </FieldCell>
                <FieldCell>
                  <TextField
                    label="Last Name"
                    placeholder={currentUser?.lastName || 'Last Name'}
                    defaultValue={currentUser?.lastName || ''}
                    type="text"
                    autoComplete="text"
                    ref={register}
                    helper={errors.lastName?.message || ''}
                    state={errors.lastName ? 'error' : 'default'}
                    name="lastName"
                  />
                </FieldCell>
                <FieldCell>
                  <Label>Phone Number</Label>
                  <Controller
                    control={control}
                    name="countryCode"
                    defaultValue={countryCode}
                    render={({value}) => (
                      <PhoneSelectField
                        placeholder="Phone number"
                        name="countryCode"
                        onChange={onChangeCountryCode}
                        defaultValue={countryCode}
                        select={value}
                        selectOptions={phoneCodeOptions}
                        setSelect={(prefix: string) => {
                          setCountryCode(prefix);
                          handleSetPrefix(prefix);
                        }}
                        type="phoneNumberDropdownInput"
                        phoneNumberInputInfo={{
                          defaultValue: currentUser?.phoneNumber || '',
                          label: 'Phone Number',
                          placeholder: 'Phone Number',
                          type: 'text',
                          autoComplete: 'text',
                          ref: register,
                          helper: errors.phoneNumber?.message || '',
                          state: errors.phoneNumber ? 'error' : 'default',
                          name: 'phoneNumber',
                        }}
                      />
                    )}
                  />
                </FieldCell>
                <FieldCell>
                  <div>
                    <Label>Email Address</Label>
                    {currentUser?.role === 'admin' &&
                      (disabled ? (
                        <ButtonWrapper>
                          <EmailButton
                            type="button"
                            kind="small"
                            onClick={emailChange}>
                            Change Email
                          </EmailButton>
                        </ButtonWrapper>
                      ) : (
                        <ButtonWrapper>
                          <CancelButton
                            type="button"
                            kind="small"
                            onClick={emailChange}>
                            Cancel
                          </CancelButton>
                          <EmailButton
                            type="button"
                            kind="small"
                            onClick={emailHandleSubmit(submitEmail)}
                            disabled={isDisabledEmail}
                            data-form-action={true}
                            isLoading={isSubmittingEmail}
                            aria-busy={isSubmittingEmail}>
                            Save
                          </EmailButton>
                        </ButtonWrapper>
                      ))}
                  </div>
                  <TextField
                    disabled={currentUser?.role !== 'admin' || disabled}
                    defaultValue={currentUser?.email || ''}
                    placeholder={currentUser?.email || 'Email address'}
                    type="email"
                    onChange={getEmail}
                    autoComplete="email"
                    ref={emailRegister}
                    helper={emailErrors.email?.message || ''}
                    state={emailErrors.email ? 'error' : 'default'}
                    name="email"
                  />
                </FieldCell>
                <FieldCell>
                  <TextField
                    defaultValue={
                      currentUser?.jobTitle
                        ? capitalize(currentUser.jobTitle)
                        : ''
                    }
                    label="Job Title"
                    placeholder={
                      currentUser?.jobTitle
                        ? capitalize(currentUser.jobTitle)
                        : 'Eg. Software developer'
                    }
                    type="text"
                    autoComplete="text"
                    ref={register}
                    helper={errors.jobTitle?.message || ''}
                    state={errors.jobTitle ? 'error' : 'default'}
                    name="jobTitle"
                  />
                </FieldCell>
                <FieldCell>
                  {group.length === 0 && <ItemLoaderLine />}
                  {group.length > 0 && !!currentUserGroups && (
                    <ResponsiveGroupField>
                      <GroupSelect
                        control={control}
                        handleSelectChangeGroups={handleSelectChangeGroups}
                        group={group}
                        currentUser={currentUser}
                      />
                    </ResponsiveGroupField>
                  )}
                </FieldCell>
                <FieldCell>
                  <Controller
                    control={control}
                    name="timezone"
                    defaultValue={normalizeTimezoneIdentifier(
                      currentUser?.timezone,
                    )}
                    render={({value}) => (
                      <SelectField
                        name="timezone"
                        placeholder="Select a time zone"
                        value={value}
                        label="Time Zone"
                        options={getTimezoneOptions()}
                        helper={errors.timezone?.message || ''}
                        state={errors.timezone ? 'error' : 'default'}
                        onChange={(data: {value: string}) => {
                          data !== null && handleSelectChange(data.value);
                        }}
                      />
                    )}
                  />
                </FieldCell>
              </DetailsGrid>
              <DetailsActions>
                <ProfileSaveChangesButton
                  type="submit"
                  width="sm"
                  disabled={isDisabled || image.disable}
                  data-form-action={true}
                  isLoading={isSubmitting}
                  aria-busy={isSubmitting}>
                  Save Changes
                </ProfileSaveChangesButton>
              </DetailsActions>
            </form>
          </DetailsCard>

          {/* Vacation/OOO Section */}
          <VacationSection>
            <VacationHeader>
              <div>
                <Body1 weight="bold" kind="textDark">
                  Vacation / Out of Office
                </Body1>
                <VerticalSpacer size="4px" />
                <Body2 kind="textBody">
                  Let teammates know when you are away.
                </Body2>
              </div>
              {activeVacation && <VacationStatus>Out of office</VacationStatus>}
            </VacationHeader>

            <VacationContent>
              {isVacationLoading ? (
                <ItemLoaderLine />
              ) : (
                <SectionCard
                  title={
                    activeVacation
                      ? 'Out of Office details'
                      : 'Set Out of Office'
                  }
                  contentStyling={{padding: '24px'}}>
                  <div>
                    <DateRow>
                      <DateField>
                        <DateLabel>First day</DateLabel>
                        <CustomDatePickerContainer disabled>
                          <Body1 kind="textBody">
                            {formatVacationDate(
                              activeVacation?.startDate ||
                                dayjs().format('YYYY-MM-DD'),
                              normalizeTimezoneIdentifier(
                                currentUser?.timezone,
                              ),
                            )}
                          </Body1>
                        </CustomDatePickerContainer>
                      </DateField>

                      <DateField>
                        <DateLabel>Last day</DateLabel>
                        <CustomDatePickerContainer
                          ref={endDateDropdown.ref}
                          disabled={Boolean(
                            activeVacation && !isEditingVacation,
                          )}
                          onClick={
                            activeVacation && !isEditingVacation
                              ? undefined
                              : (e) => endDateDropdown.handleOpen(e as any)
                          }>
                          <Body1 kind="textBody">
                            {vacationEndDate
                              ? dayjs(vacationEndDate).format('DD/MM/YYYY')
                              : 'Select date'}
                          </Body1>
                          {(!activeVacation || isEditingVacation) && (
                            <CalendarIcon />
                          )}
                          {endDateDropdown.open &&
                            (!activeVacation || isEditingVacation) && (
                              <DropdownWrapper>
                                <Calendar
                                  onChange={handleEndDateSelect}
                                  value={vacationEndDate}
                                  disabledBefore={
                                    activeVacation?.startDate ||
                                    dayjs().format('YYYY-MM-DD')
                                  }
                                />
                              </DropdownWrapper>
                            )}
                        </CustomDatePickerContainer>
                      </DateField>
                    </DateRow>

                    {(!activeVacation || isEditingVacation) && (
                      <VacationReason>
                        <TextField
                          label="Reason"
                          optional
                          placeholder="e.g. Annual leave"
                          value={vacationReason}
                          onChange={(event) =>
                            setVacationReason(event.target.value)
                          }
                          name="vacationReason"
                        />
                      </VacationReason>
                    )}

                    {activeVacation &&
                      !isEditingVacation &&
                      activeVacation.reason && (
                        <VacationReason>
                          <Body2 weight="semibold" kind="textBody">
                            Reason
                          </Body2>
                          <VacationReasonValue>
                            <Body1 kind="textDark">
                              {activeVacation.reason}
                            </Body1>
                          </VacationReasonValue>
                        </VacationReason>
                      )}

                    <VerticalSpacer size="24px" />
                    <SaveButtonWrapper>
                      {!activeVacation && (
                        <ProfileSaveChangesButton
                          type="button"
                          kind="primary"
                          onClick={handleSaveVacation}
                          isLoading={isSavingVacation}
                          aria-busy={isSavingVacation}
                          disabled={
                            !isVacationEndDateValid(
                              vacationEndDate,
                              dayjs().format('YYYY-MM-DD'),
                            )
                          }>
                          Set Out of Office
                        </ProfileSaveChangesButton>
                      )}

                      {activeVacation && !isEditingVacation && (
                        <>
                          <Button
                            type="button"
                            kind="secondary"
                            onClick={() => setIsEditingVacation(true)}
                            style={{padding: '10px 16px'}}>
                            Edit dates
                          </Button>
                          <DangerButton
                            type="button"
                            kind="secondary"
                            onClick={handleEndVacation}
                            isLoading={isEndingVacation}
                            style={{padding: '10px 16px'}}>
                            End Out of Office
                          </DangerButton>
                        </>
                      )}

                      {activeVacation && isEditingVacation && (
                        <>
                          <Button
                            type="button"
                            kind="secondary"
                            onClick={handleCancelVacationEdit}
                            style={{padding: '10px 16px'}}>
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            kind="primary"
                            onClick={handleSaveVacation}
                            isLoading={isSavingVacation}
                            disabled={
                              !isVacationEndDateValid(
                                vacationEndDate,
                                activeVacation.startDate,
                              )
                            }
                            style={{padding: '10px 16px'}}>
                            Save changes
                          </Button>
                        </>
                      )}
                    </SaveButtonWrapper>
                  </div>
                </SectionCard>
              )}
            </VacationContent>
          </VacationSection>
        </ProfileStack>
      )}
      {isFetchingCurrentUser && <ItemLoader />}
    </>
  );
});
